import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Paper
} from '@mui/material';

export default function DocumentPlanModal({
  open,
  loading,
  planDocument,
  error,
  onApprove,
  onClose
}) {
  const planPages = planDocument?.pages || [];
  const pageCount = planDocument?.total_pages ?? planPages.length;
  const hasPlan = Boolean(planPages.length);
  const planSummary = planDocument?.generation_notes || {};
  const documentTitle = planDocument?.document_title || 'Untitled document';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { minHeight: '60vh' } }}
    >
      <DialogTitle>Review the generated plan</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 6, textAlign: 'center' }} spacing={1}>
            <CircularProgress />
            <Typography variant="body1">Fetching the page-by-page outline...</Typography>
            <Typography variant="body2" color="text.secondary">
              This may take a few moments while the agent scans the curriculum and builds the plan.
            </Typography>
          </Stack>
        ) : error ? (
          <Typography variant="body2" color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        ) : (
          <>
            <Box sx={{ mb: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle1">
                  {documentTitle}
                </Typography>
                <Chip label={`Pages: ${pageCount}`} size="small" />
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Audience: {planDocument?.target_audience || 'Unspecified'} | Qualification: {planDocument?.qualification_or_module_code || 'N/A'}
              </Typography>
              {planSummary.search_strategy && (
                <Typography variant="caption" color="text.secondary">
                  Search strategy: {planSummary.search_strategy}
                </Typography>
              )}
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {hasPlan ? 'Each planned page below will become a PDF-ready section once approved.' : 'No pages were outlined yet; consider generating again.'}
            </Typography>

            <Box sx={{ maxHeight: 340, overflowY: 'auto' }}>
              {hasPlan ? (
                planPages.map((page, index) => (
                  <Paper
                    key={`plan-page-${page.page_number || index}`}
                    variant="outlined"
                    sx={{ p: 2, mb: 1 }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                      <Typography variant="subtitle2">
                        Page {page.page_number || index + 1}: {page.title || 'Proposed content'}
                      </Typography>
                      {page.approximate_word_count ? (
                        <Chip label={`${page.approximate_word_count} words`} size="small" />
                      ) : null}
                    </Stack>
                    {page.outline_path && page.outline_path.length > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        Outline: {page.outline_path.join(' › ')}
                      </Typography>
                    )}
                    {page.summary && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {page.summary}
                      </Typography>
                    )}
                  </Paper>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  The agent did not return a page outline. Try regenerating the plan.
                </Typography>
              )}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onApprove}
          disabled={loading || !planDocument || !hasPlan}
        >
          Approve plan & start generation
        </Button>
      </DialogActions>
    </Dialog>
  );
}

